import { OktaAuth } from "@okta/okta-auth-js";

import { AuthEndpoints } from "@generated/auth";
import { oktaConfig } from "src/domains/auth/configs/oktaConfig";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { StorageKeys } from "src/platform/constants/Storage";
import API from "src/platform/http/shared-http";
import { IUser } from "src/platform/types/shared-types";
import debugLogger from "src/platform/utils/debugLogger";
import {
  getItem,
  setItem,
  clearAllRushaStorage,
} from "src/platform/utils/storage";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResult {
  user?: IUser;
}

/**
 * Centralized Authentication Service
 * Handles all authentication-related operations.
 *
 * The session JWT lives in an httpOnly cookie set by the API. The UI never
 * touches the token. This service manages the login/logout flow and stores
 * non-sensitive user metadata in localStorage for UI display.
 */
class AuthService {
  private oktaAuth: OktaAuth;

  constructor() {
    this.oktaAuth = new OktaAuth(oktaConfig);
  }

  /**
   * Initialize authentication state from storage.
   * The token is NOT stored here — it's in an httpOnly cookie. We only
   * check if we have user metadata to determine if a session might exist.
   * The actual session validation happens via GET /auth/me.
   */
  initializeAuth(): { user: IUser | null } {
    const storedUser = getItem(StorageKeys.USER);
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    return { user: parsedUser };
  }

  /**
   * Store user metadata after login. The JWT is in the httpOnly cookie,
   * not in localStorage. We only store non-sensitive user info for UI display.
   */
  storeAuthResult(user: IUser): void {
    // Clear previous org session — a new login starts without an org context.
    orgSessionManager.clearSession();
    localStorage.removeItem(StorageKeys.SELECTED_ORGANIZATION);

    if (user) {
      setItem(StorageKeys.USER, JSON.stringify(user));
    }
  }

  /**
   * Direct login with credentials (email/password).
   * The API sets the httpOnly session cookie on success.
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      const api = new API(baseUrl, AuthEndpoints.LOGIN);
      const response = await api.post<AuthResult>(credentials);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((response as any).error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error((response as any).message || "Login failed");
      }
      this.storeAuthResult((response as AuthResult).user as IUser);
      return response as AuthResult;
    } catch (error) {
      debugLogger.error("Login error:", error);
      throw error;
    }
  }

  /**
   * Process Okta authentication result
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processOktaAuth(authState: any): AuthResult {
    if (!authState.isAuthenticated) {
      throw new Error("Not authenticated with Okta");
    }

    const { idToken } = authState.idToken;

    // Create user object from Okta claims
    const user: IUser = {
      email: idToken.claims.email,
      firstName: idToken.claims.given_name,
      lastName: idToken.claims.family_name,
    };

    this.storeAuthResult(user);
    setItem(StorageKeys.OKTA_AUTH, "true");

    return { user };
  }

  /**
   * Process OAuth token received from callback.
   * With cookie-based auth, the token is already in the httpOnly cookie.
   * We just need to fetch the user info.
   */
  processOAuthToken(_token: string): AuthResult {
    // Token is in the httpOnly cookie — we don't store it.
    // The caller should fetch /auth/me to get user info.
    return {};
  }

  /**
   * Initialize Okta sign-in with redirect
   */
  async loginWithOkta(): Promise<void> {
    try {
      await this.oktaAuth.signInWithRedirect();
    } catch (error) {
      debugLogger.error("Okta login error:", error);
      throw error;
    }
  }

  /**
   * Logout user and clear auth state.
   * Calls the API logout endpoint to clear the httpOnly cookie,
   * then clears client-side state.
   */
  async logout(): Promise<void> {
    let logoutUrl: string | undefined;
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      const api = new API(baseUrl, "/api/v1/auth/logout");
      const response = await api.post<{ logoutUrl?: string }>({});
      logoutUrl = response?.logoutUrl;
    } catch (error) {
      debugLogger.error("Logout error:", error);
    } finally {
      orgSessionManager.clearSession();
      clearAllRushaStorage();
    }

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  /**
   * Check if user is authenticated by checking for user metadata.
   * Actual session validity is verified via GET /auth/me.
   */
  isAuthenticated(): boolean {
    return !!getItem(StorageKeys.USER);
  }

  async signup(userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<AuthResult> {
    const baseUrl = apiBaseUrls.BACKEND;
    const api = new API(baseUrl, AuthEndpoints.SIGNUP);
    const response = await api.post<AuthResult>(userData);
    if ((response as { error?: string }).error) {
      throw new Error(
        (response as { message?: string }).message || "Sign up failed",
      );
    }
    this.storeAuthResult((response as AuthResult).user as IUser);
    return response as AuthResult;
  }

  async githubLoginLink(): Promise<string> {
    const encodedURL = encodeURIComponent(window.location.origin);
    const api = new API(
      apiBaseUrls.BACKEND,
      `${AuthEndpoints.GITHUB_LOGIN_LINK}?redirect_uri=${encodedURL}`,
    );
    return api.get() as Promise<string>;
  }

  async googleLoginLink(): Promise<string> {
    const encodedURL = encodeURIComponent(window.location.origin);
    const api = new API(
      apiBaseUrls.BACKEND,
      `${AuthEndpoints.GOOGLE_LOGIN_LINK}?redirect_uri=${encodedURL}`,
    );
    return api.get() as Promise<string>;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
